export function expectDefined<T>(value: T | undefined | null): T {
  if (value === undefined || value === null) {
    throw new Error('Expected defined value');
  }
  return value;
}

export function assertDefined<T>(value: T | undefined): asserts value is NonNullable<T> {
  if (value === undefined || value === null) {
    throw new Error('Expected defined value');
  }
}

export function assertString(value: unknown): asserts value is string {
  if (typeof value !== 'string') {
    throw new Error('Expected string');
  }
}

export function assertCondition(value: boolean, message?: string): void {
  if (!value) {
    throw new Error(message ?? 'Expected condition to be true');
  }
}

export function assertInstanceOf<T>(
  value: unknown,
  constructor: abstract new (...args: never[]) => T
): asserts value is T {
  if (!(value instanceof constructor)) {
    throw new Error(`Expected instance of ${constructor.name}`);
  }
}

export function hasAnyKeys<K, V>(value: Map<K, V>, keys: K[]): boolean;
export function hasAnyKeys<T extends object, K, V>(
  value: T | Map<K, V>,
  keys: (keyof T)[] | K[]
): boolean {
  if (value instanceof Map) {
    for (const key of keys as K[]) {
      if (value.has(key)) {
        return true;
      }
    }
  } else {
    for (const key of keys as (keyof T)[]) {
      if (key in value) {
        return true;
      }
    }
  }
  return false;
}

export function assertDoesNotHaveAnyKeys<K, V>(value: Map<K, V>, keys: K[]): void;
export function assertDoesNotHaveAnyKeys<T extends object>(
  value: T,
  keys: (keyof T)[]
): asserts value is T & { [K in keyof T]: K extends keyof T ? never : T[K] };
export function assertDoesNotHaveAnyKeys<T extends object, K, V>(
  value: T | Map<K, V>,
  keys: (keyof T)[] | K[]
): void {
  if (value instanceof Map) {
    for (const key of keys as K[]) {
      if (value.has(key)) {
        throw new Error(`Expected map to not have key ${String(key)}`);
      }
    }
  } else {
    for (const key of keys as (keyof T)[]) {
      if (key in value) {
        throw new Error(`Expected object to not have key ${String(key)}`);
      }
    }
  }
}

export function assertIsAtLeast(value: number, min: number, message?: string): void {
  if (value < min) {
    throw new Error(message ?? `Value must be at least ${min}`);
  }
}

export function assertIsAtMost(value: number, max: number, message?: string): void {
  if (value > max) {
    throw new Error(message ?? `Value must be at most ${max}`);
  }
}

/**
 * A convenience method to avoid the
 * `eslint@typescript-eslint/no-unused-expressions` error when you want to
 * simply detect a change in the value in a reactive statement without using it.
 */
export function reactTo(...values: unknown[]) {
  for (const value of values) {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    value;
  }
}

/**
 * @returns a boolean indicating whether the two Sets have exactly the same elements.
 */
export function areSetsEqual<T>(a: Set<T>, b: Set<T>): boolean {
  return a.size === b.size && a.isSubsetOf(b);
}

/**
 * Converts a value to its JSON representation if it has a `toJSON` method.
 *
 * @param value - The value to convert to JSON. If the value is an object with a `toJSON` method,
 *                that method will be called to obtain the JSON representation.
 * @returns The JSON representation of the value if it has a `toJSON` method, otherwise the original value.
 */
export function toJSON(value: unknown, opts?: unknown): unknown {
  if (value && typeof value === 'object' && 'toJSON' in value) {
    const v = value.toJSON as (opts: unknown) => unknown;
    if (typeof v === 'function') {
      return v.bind(value)(opts);
    }
  }
  return value;
}

/**
 * Returns the given value if it is not empty, as determined by the `isEmpty`
 * function, otherwise returns `undefined`.
 *
 * @template T - The type of the value.
 * @param {T} value - The value to check for emptiness.
 * @returns {T | undefined} - The value if it is not empty, otherwise
 * `undefined`.
 */
export function nonEmpty<T>(value: T): T | undefined {
  if (isEmpty(value)) {
    return undefined;
  } else {
    return value;
  }
}

/**
 * Checks if a given value is empty.
 *
 * A value is considered empty if it is:
 * - `undefined`
 * - `null`
 * - An empty string
 * - An object with no own enumerable properties (after removing empty attributes)
 * - An empty array
 * - An empty Set
 * - An empty Map
 *
 * @param value - The value to check for emptiness.
 * @returns `true` if the value is empty, otherwise `false`.
 */
export function isEmpty(value: unknown): boolean {
  if (value === undefined || value === null) {
    return true;
  }
  if (typeof value === 'string' && value.length === 0) {
    return true;
  }
  if (
    typeof value === 'object' &&
    Object.keys(removingEmptyAttributes(value as Record<string, unknown>)).length === 0
  ) {
    return true;
  }
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }
  if (value instanceof Set && value.size === 0) {
    return true;
  }
  if (value instanceof Map && value.size === 0) {
    return true;
  }
  return false;
}

/**
 * Removes empty attributes from an object.
 *
 * This function takes an optional object and returns a new object
 * with all properties that have empty values removed. An empty value
 * is determined by the `isEmpty` function.
 *
 * @param obj - The object from which to remove empty attributes.
 * @returns A new object with empty attributes removed. If the input
 * object is `undefined`, an empty object is returned.
 */
export function removingEmptyAttributes(
  obj?: Record<string, unknown>
): Record<string, unknown> {
  if (obj === undefined) {
    return {};
  }
  const result: Record<string, unknown> = {};
  Object.keys(obj).forEach(key => {
    const value = obj[key];
    if (!isEmpty(value)) {
      result[key] = obj[key];
    }
  });
  return result;
}
